import { useState, useEffect, useCallback } from 'react';
import { api, formatCurrency, getCurrentMonth } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { CalendarDays, Save, ChevronLeft, ChevronRight, Loader2, Trash2, History, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const MOIS_LABELS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const formatMoisLabel = (mois) => {
  if (!mois) return '';
  const [y, m] = mois.split('-');
  return `${MOIS_LABELS[parseInt(m, 10) - 1]} ${y}`;
};

const navigateMois = (mois, delta) => {
  const [y, m] = mois.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const SaisieMensuelle = ({ salaries = [] }) => {
  const [mois, setMois] = useState(getCurrentMonth());
  const [saisies, setSaisies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [forms, setForms] = useState({});
  const [historyId, setHistoryId] = useState(null);
  const [history, setHistory] = useState([]);
  const [recap, setRecap] = useState(null);

  const employes = salaries.filter(s => s.type_contrat !== 'dirigeant_tns');

  const loadData = useCallback(async (currentMois, currentEmployes) => {
    setLoading(true);
    try {
      const [saisiesRes, recapRes] = await Promise.all([
        api.get(`/paie/saisie-mensuelle?mois=${currentMois}`),
        api.get(`/paie/saisie-mensuelle/recap/${currentMois}`),
      ]);
      const data = saisiesRes.data.saisies || [];
      setSaisies(data);
      setRecap(recapRes.data);

      // Pre-fill forms: existing saisies + blank forms for employees without one
      const newForms = {};
      for (const emp of currentEmployes) {
        const existing = data.find(s => s.salary_id === emp.id);
        if (existing) {
          newForms[emp.id] = { ...existing };
        } else {
          newForms[emp.id] = {
            salary_id: emp.id,
            nom: emp.nom,
            mois: currentMois,
            salaire_net: '',
            salaire_brut: '',
            charges_patronales: '',
            charges_salariales: '',
            prime: '',
            conges_pris: '',
            heures_travaillees: '',
            heures_supplementaires: '',
            notes: '',
          };
        }
      }
      setForms(newForms);
    } catch {
      toast.error('Erreur chargement saisies');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (employes.length > 0) {
      loadData(mois, employes);
    }
  }, [mois, salaries]);

  const handleFieldChange = (empId, field, value) => {
    setForms(prev => ({
      ...prev,
      [empId]: { ...prev[empId], [field]: value }
    }));
  };

  const handleSave = async (empId) => {
    const form = forms[empId];
    if (!form) return;

    setSaving(prev => ({ ...prev, [empId]: true }));
    try {
      const payload = {
        salary_id: empId,
        mois,
        salaire_net: parseFloat(form.salaire_net) || 0,
        salaire_brut: parseFloat(form.salaire_brut) || 0,
        charges_patronales: parseFloat(form.charges_patronales) || 0,
        charges_salariales: parseFloat(form.charges_salariales) || 0,
        prime: parseFloat(form.prime) || 0,
        conges_pris: parseFloat(form.conges_pris) || 0,
        heures_travaillees: parseFloat(form.heures_travaillees) || 0,
        heures_supplementaires: parseFloat(form.heures_supplementaires) || 0,
        notes: form.notes || '',
      };
      const res = await api.post('/paie/saisie-mensuelle', payload);
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(res.data.message);
        loadData(mois, employes);
      }
    } catch {
      toast.error('Erreur sauvegarde');
    }
    setSaving(prev => ({ ...prev, [empId]: false }));
  };

  const handleDelete = async (empId) => {
    const existing = saisies.find(s => s.salary_id === empId);
    if (!existing?.id) return;
    try {
      await api.delete(`/paie/saisie-mensuelle/${existing.id}`);
      toast.success('Saisie supprimée');
      loadData(mois, employes);
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const loadHistory = async (empId) => {
    if (historyId === empId) { setHistoryId(null); return; }
    setHistoryId(empId);
    try {
      const res = await api.get(`/paie/saisie-mensuelle/employe/${empId}`);
      setHistory(res.data.saisies || []);
    } catch { setHistory([]); }
  };

  const hasData = (empId) => {
    return saisies.some(s => s.salary_id === empId);
  };

  const isModified = (empId) => {
    const form = forms[empId];
    const existing = saisies.find(s => s.salary_id === empId);
    if (!form) return false;
    if (!existing) {
      return (parseFloat(form.salaire_net) || 0) > 0;
    }
    const fields = ['salaire_net', 'salaire_brut', 'charges_patronales', 'charges_salariales', 'prime', 'conges_pris'];
    return fields.some(f => (parseFloat(form[f]) || 0) !== (existing[f] || 0));
  };

  if (employes.length === 0) {
    return (
      <Card className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid="saisie-mensuelle-empty">
        <CardContent className="py-8 text-center">
          <AlertCircle size={24} className="mx-auto mb-2 text-[rgb(var(--lm-text-muted))]" />
          <p className="text-sm text-[rgb(var(--lm-text-secondary))]">Ajoutez des salariés pour commencer la saisie mensuelle</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[rgb(var(--lm-surface))] border-blue-400/20 animate-slide-up" data-testid="saisie-mensuelle-section">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <CardTitle className="text-base text-[rgb(var(--lm-text))] flex items-center gap-2">
            <CalendarDays size={16} className="text-blue-400" />
            Saisie Paie Mensuelle
          </CardTitle>
          <div className="flex items-center gap-1.5" data-testid="mois-navigator">
            <button
              onClick={() => setMois(navigateMois(mois, -1))}
              className="p-1.5 rounded-lg bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))] hover:text-[rgb(var(--lm-text))] transition-colors"
              data-testid="prev-month-btn"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="px-3 py-1.5 rounded-lg bg-[rgb(var(--lm-surface-elevated))] min-w-[140px] text-center">
              <span className="text-sm font-medium text-[rgb(var(--lm-text))]" data-testid="current-month-label">
                {formatMoisLabel(mois)}
              </span>
            </div>
            <button
              onClick={() => setMois(navigateMois(mois, 1))}
              className="p-1.5 rounded-lg bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))] hover:text-[rgb(var(--lm-text))] transition-colors"
              data-testid="next-month-btn"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8" data-testid="saisie-loading">
            <Loader2 size={24} className="animate-spin text-blue-400" />
          </div>
        ) : (
          <>
            {/* Employee forms */}
            <div className="space-y-3">
              {employes.map((emp) => {
                const form = forms[emp.id] || {};
                const saved = hasData(emp.id);
                const modified = isModified(emp.id);
                const isSaving = saving[emp.id];

                return (
                  <div key={emp.id} className="border border-[rgb(var(--lm-border))] rounded-xl overflow-hidden" data-testid={`saisie-row-${emp.id}`}>
                    {/* Employee header */}
                    <div className={`px-4 py-2.5 flex items-center justify-between ${saved ? 'bg-blue-400/5' : 'bg-[rgb(var(--lm-bg))]/30'}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${saved ? 'bg-blue-400' : 'bg-[rgb(var(--lm-text-muted))]'}`} />
                        <span className="text-sm font-medium text-[rgb(var(--lm-text))]">{emp.nom}</span>
                        {saved && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-400/10 text-blue-400">Saisi</span>}
                        {modified && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-400/10 text-orange-400">Modifié</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => loadHistory(emp.id)}
                          className="p-1 rounded text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))] transition-colors"
                          title="Historique"
                          data-testid={`history-btn-${emp.id}`}
                        >
                          <History size={14} />
                        </button>
                        {saved && (
                          <button
                            onClick={() => handleDelete(emp.id)}
                            className="p-1 rounded text-red-400/50 hover:text-red-400 transition-colors"
                            title="Supprimer"
                            data-testid={`delete-saisie-btn-${emp.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Form fields */}
                    <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      <div>
                        <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Net</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={form.salaire_net ?? ''}
                          onChange={(e) => handleFieldChange(emp.id, 'salaire_net', e.target.value)}
                          className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-sm h-9"
                          placeholder="0.00"
                          data-testid={`input-net-${emp.id}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Brut</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={form.salaire_brut ?? ''}
                          onChange={(e) => handleFieldChange(emp.id, 'salaire_brut', e.target.value)}
                          className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-sm h-9"
                          placeholder="0.00"
                          data-testid={`input-brut-${emp.id}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Ch. Patron.</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={form.charges_patronales ?? ''}
                          onChange={(e) => handleFieldChange(emp.id, 'charges_patronales', e.target.value)}
                          className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-sm h-9"
                          placeholder="0.00"
                          data-testid={`input-charges-pat-${emp.id}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Ch. Salar.</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={form.charges_salariales ?? ''}
                          onChange={(e) => handleFieldChange(emp.id, 'charges_salariales', e.target.value)}
                          className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-sm h-9"
                          placeholder="0.00"
                          data-testid={`input-charges-sal-${emp.id}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Primes</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={form.prime ?? ''}
                          onChange={(e) => handleFieldChange(emp.id, 'prime', e.target.value)}
                          className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-sm h-9"
                          placeholder="0.00"
                          data-testid={`input-prime-${emp.id}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Congés (j)</label>
                        <Input
                          type="number"
                          step="0.5"
                          value={form.conges_pris ?? ''}
                          onChange={(e) => handleFieldChange(emp.id, 'conges_pris', e.target.value)}
                          className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-sm h-9"
                          placeholder="0"
                          data-testid={`input-conges-${emp.id}`}
                        />
                      </div>
                    </div>

                    {/* Save button row */}
                    <div className="px-4 pb-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Input
                          value={form.notes ?? ''}
                          onChange={(e) => handleFieldChange(emp.id, 'notes', e.target.value)}
                          className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8 w-48 sm:w-64"
                          placeholder="Notes (optionnel)"
                          data-testid={`input-notes-${emp.id}`}
                        />
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleSave(emp.id)}
                        disabled={isSaving}
                        className={`h-8 text-xs ${saved ? 'bg-blue-500 hover:bg-blue-600' : 'bg-[rgb(var(--lm-gold))] hover:opacity-90'} text-white`}
                        data-testid={`save-saisie-btn-${emp.id}`}
                      >
                        {isSaving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                        {saved ? 'Mettre à jour' : 'Enregistrer'}
                      </Button>
                    </div>

                    {/* History panel */}
                    {historyId === emp.id && (
                      <div className="border-t border-[rgb(var(--lm-border))] bg-[rgb(var(--lm-bg))]/50 px-4 py-3" data-testid={`history-panel-${emp.id}`}>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-2 flex items-center gap-1">
                          <FileText size={10} /> Historique des saisies
                        </p>
                        {history.length === 0 ? (
                          <p className="text-xs text-[rgb(var(--lm-text-muted))]">Aucune saisie précédente</p>
                        ) : (
                          <div className="space-y-1.5 max-h-48 overflow-y-auto">
                            {history.map((h, i) => (
                              <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-[rgb(var(--lm-border)/0.3)] last:border-0">
                                <span className="text-[rgb(var(--lm-text-secondary))] font-medium">{formatMoisLabel(h.mois)}</span>
                                <div className="flex items-center gap-4">
                                  <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(h.salaire_net)} net</span>
                                  <span className="font-mono text-orange-400">{formatCurrency(h.charges_patronales)} ch.</span>
                                  {h.conges_pris > 0 && <span className="text-[rgb(var(--lm-text-muted))]">{h.conges_pris}j congés</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Monthly recap */}
            {recap && recap.nb_salaries > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4" data-testid="recap-mensuel">
                <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase">Total Net</p>
                  <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{formatCurrency(recap.total_net)}</p>
                </div>
                <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase">Total Brut</p>
                  <p className="font-mono text-lg text-[rgb(var(--lm-text-secondary))]">{formatCurrency(recap.total_brut)}</p>
                </div>
                <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-orange-400 uppercase">Ch. Patronales</p>
                  <p className="font-mono text-lg text-orange-400">{formatCurrency(recap.total_charges_patronales)}</p>
                </div>
                <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase">Congés pris</p>
                  <p className="font-mono text-lg text-[rgb(var(--lm-text-secondary))]">{recap.total_conges_pris}j</p>
                </div>
                <div className="bg-blue-400/5 border border-blue-400/20 rounded-lg p-3 text-center col-span-2 md:col-span-1">
                  <p className="text-[10px] text-blue-400 uppercase font-semibold">Coût Employeur</p>
                  <p className="font-mono text-lg text-blue-400 font-bold">{formatCurrency(recap.cout_total_employeur)}</p>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SaisieMensuelle;
